// frontend/src/pages/ClientReports.tsx
// Версия: 2.0.0 — используем нативный TextField type="date" вместо DatePicker
// Причина: несовместимость @mui/x-date-pickers с date-fns v3/v4
// FIX: совместимость с MUI DataGrid v6+ (valueGetter signature)
import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button, Grid, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { BackendService } from '../services/BackendService';
import { getRequestStatusLabel, normalizeRequestStatus } from '../utils/statusLabels';
import { apiUrl } from '../config/paths';

interface ReportRow {
  id: number;
  request_number?: string;
  ssas_number?: string;
  vessel_name?: string;
  imo_number?: string;
  mmsi?: string;
  planned_test_date?: string;
  test_date?: string;
  signal_received_at?: string;
  status?: string;
  result?: string;
  confirmation_id?: number;
}

const ClientReports: React.FC = () => {
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const getRowDate = (row: ReportRow): string | undefined => {
    return row.test_date || row.planned_test_date || row.signal_received_at;
  };

  // Загрузка данных
  const loadData = async () => {
    setLoading(true);
    try {
      // Используем getMyRequests - возвращает только заявки текущего пользователя
      const response = await BackendService.getMyRequests();
      const allData = normalizeList<ReportRow>(response);

      // v2.1.0: Отчёты — только финальные результаты (confirmed/rejected)
      const data = allData.filter((r) => {
        const ns = normalizeRequestStatus(String(r.status || ''));
        return ['confirmed', 'rejected'].includes(ns);
      });

      // Фильтруем по датам если заданы
      let filtered = data;

      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        filtered = filtered.filter((r) => {
          const d = getRowDate(r);
          if (!d) return false;
          return new Date(d) >= fromDate;
        });
      }

      if (dateTo) {
        const toDate = new Date(dateTo);
        filtered = filtered.filter((r) => {
          const d = getRowDate(r);
          if (!d) return false;
          return new Date(d) <= toDate;
        });
      }

      setRows(filtered);
    } catch (error) {
      console.error('Ошибка загрузки отчётов:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Колонки таблицы
  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'request_number', headerName: '№ заявки', width: 120 },
    { field: 'ssas_number', headerName: 'Терминал SSAS', width: 140 },
    { field: 'vessel_name', headerName: 'Судно', width: 160 },
    { field: 'imo_number', headerName: 'IMO', width: 100 },
    { field: 'mmsi', headerName: 'MMSI', width: 110 },
    {
      field: 'test_date',
      headerName: 'Дата теста',
      width: 120,
      // FIX: MUI DataGrid v6+ signature (value, row) instead of (params)
      valueGetter: (_value, row) => {
        if (!row) return '';
        const r = row as ReportRow;
        const date = getRowDate(r);
        if (!date) return '';
        return new Date(date).toLocaleDateString('ru-RU');
      },
    },
    {
      field: 'status',
      headerName: 'Статус',
      width: 140,
      // FIX: MUI DataGrid v6+ signature (value, row) instead of (params)
      valueGetter: (_value, row) => {
        if (!row) return '';
        const r = row as ReportRow;
        return getRequestStatusLabel(String(r.status || ''));
      },
    },
    {
      field: 'pdf',
      headerName: 'PDF',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (_params: any) => {
        const row = _params.row as ReportRow;
        const ns = normalizeRequestStatus(String(row.status || ''));
        if (!row.confirmation_id || ns === 'rejected') return null;
        return (
          <IconButton
            size="small"
            title="Открыть PDF подтверждения"
            onClick={() => {
              window.open(apiUrl(`confirmations/${row.confirmation_id}/pdf`), '_blank');
            }}
          >
            <PictureAsPdfIcon color="error" />
          </IconButton>
        );
      },
    },
  ];

  // Сброс фильтров
  const handleReset = () => {
    setDateFrom('');
    setDateTo('');
    loadData();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Мои отчёты
      </Typography>

      {/* Фильтры по дате — нативный input */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <TextField
              label="Дата с"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              sx={{ width: 160 }}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Дата по"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              sx={{ width: 160 }}
            />
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={loadData}>
              Применить
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" onClick={handleReset}>
              Сбросить
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Таблица */}
      <Paper sx={{ p: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          autoHeight
          localeText={{
            noRowsLabel: 'Нет данных',
            MuiTablePagination: {
              labelRowsPerPage: 'Строк на странице',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

// Утилита нормализации
function normalizeList<T extends object>(input: unknown): T[] {
  if (Array.isArray(input)) return input as T[];
  if (input && typeof input === 'object') {
    const obj = input as { success?: boolean; data?: unknown };
    if (obj.success && Array.isArray(obj.data)) {
      return obj.data as T[];
    }
    const maybeData = (input as { data?: unknown }).data;
    if (Array.isArray(maybeData)) return maybeData as T[];
  }
  return [];
}

export default ClientReports;
